<template>
  <div>
    <v-alert v-if="error" type="error" class="mb-4">
      {{ error }}
    </v-alert>

    <BasicCard :title="windowTitle">
      <template v-if="earnedRank">
        <h4 class="mb-2">{{earnedRankText}}</h4>
        <p v-if="nextGoalRank" class="mb-1">{{ nextRankText }}</p>
      </template>
      <div class="mb-1" v-for="metric in metrics" :key="metric.metric">
        <ProgressMetric
            :key="metric.metric"
            v-if="!shouldHideMetric(metric)"
            :title="metric.title"
            :value="metricData[metric.metric]?.value"
            :max="metricData[metric.metric]?.max"
            :color="metric.color || props.styleDefaults?.color"
            :bgColor="metric.bgColor || props.styleDefaults?.bgColor"
            :rounded="metric.rounded "
            :type="metric?.type || props.styleDefaults?.type"
            :colorDone="metric?.colorDone || props.styleDefaults?.colorDone"
            :striped="metric?.striped"
            :labelIsPercent="metric?.labelIsPercent "
            :size="metric?.size || props.styleDefaults?.size"
            :titleClass="metric?.titleClass || props.styleDefaults?.titleClass"
            :labelClass="metric?.labelClass || props.styleDefaults?.titleClass"
            :loading="loading"
            :data-loading="loading ? 'true' : undefined"
            :noCheckbox="false"
        />
      </div>


      <div v-if="earnedRank?.description" class="mt-3" v-html="earnedRank.description" />
    </BasicCard>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import BasicCard from './base/BasicCard.vue';
import ProgressMetric from './ProgressMetric.vue';
import { fetchFullProperties } from '@/utils/fetch-full-properties-service';
import { apiErrorToString } from '@ui-api3-sdk/api/api3';
import {
  advancedDefaults,type AdvancedMetricDefinition, type AdvancedRankLevel,
  type AdvancedRankProgressProps,
  evaluateExpression,
  extractMetricNames
} from './RankProgressAdvanced.ts';
import {useLocalization} from "@/composables";
const { lt } = useLocalization('uiRankProgress');

const props = withDefaults(defineProps<AdvancedRankProgressProps>(), advancedDefaults);

const error = ref('');
const loading = ref(false);
const metricData = ref<Record<string, { value: number; max: number }>>({});
const earnedRank = ref<typeof props.ranks[0] | null>(null);
const nextGoalRank = ref<typeof props.ranks[0] | null>(null);
const fetchedItem = ref<Record<string, { raw: number | string }> | undefined>(undefined);

const currentDataItem = computed(() => props.dataItem || fetchedItem.value);

const context = computed(() => {
  const result: Record<string, number> = {};
  if (!currentDataItem.value) return result;
  for (const key in currentDataItem.value) {
    result[key] = Number(currentDataItem.value[key]?.raw ?? 0);
  }
  return result;
});

const metricsMap = computed(() => {
  const map: Record<string, AdvancedMetricDefinition> = {};
  for (const m of props.metrics) {
    map[m.metric] = m;
  }
  return map;
});

const windowTitle = computed(() => props.title || 'Rank Progress');

const nextRankText = computed(() =>
    props.nextRankTemplate?.replace('{{title}}', nextGoalRank.value?.title ?? '') ||
    `${lt('next_rank')}: ${nextGoalRank.value?.title ?? ''}`,
);
const earnedRankText = computed(() =>
    props.earnedRankTemplate?.replace('{{title}}', earnedRank.value?.title ?? '') ||
    `${lt('earned_rank')}: ${earnedRank.value?.title ?? ''}`,
);

function calculateMetricData() {
  const result: Record<string, { value: number; max: number }> = {};
  if (!context.value || Object.keys(context.value).length === 0) {
    return result;
  }

  for (const metric of props.metrics) {
    const value = evaluateExpression(metric.value, context.value);

    let max = 0;
    if (metric.max) {
      max = evaluateExpression(metric.max, context.value);
    } else if (nextGoalRank.value && nextGoalRank.value.levels[metric.metric]) {
      max = evaluateExpression(nextGoalRank.value.levels[metric.metric], context.value);
    }

    result[metric.metric] = { value, max };
  }
  return result;
}

function shouldHideMetric(metric: AdvancedMetricDefinition) {
  const val = metricData.value[metric.metric];
  return val && val.max <= 0;
}

function evaluateRank(rank: AdvancedRankLevel): boolean {
  if (!context.value || Object.keys(context.value).length === 0) {
    return false;
  }

  try {
    return Object.entries(rank.levels).every(([metricKey, thresholdExpr]) => {
      const metricDef = metricsMap.value[metricKey];
      if (!metricDef) {
        console.warn(`Метрика ${metricKey} не найдена в metrics[]`);
        return false;
      }

      const value = evaluateExpression(metricDef.value, context.value);
      const threshold = evaluateExpression(thresholdExpr, context.value);
      return value >= threshold;
    });
  } catch (e) {
    console.warn('Ошибка при оценке ранга', rank.title, e);
    return false;
  }
}

function determineRanks() {
  const passed = props.ranks.filter(evaluateRank);

  if (passed.length > 0) {
    earnedRank.value = passed[passed.length - 1];
    nextGoalRank.value = props.ranks.find(r => r.id === earnedRank.value!.nextRank) ?? null;
  } else {
    earnedRank.value = null;
    nextGoalRank.value = props.ranks[0] || null;
  }

}

watch(context, () => {
  try {
    determineRanks();
    metricData.value = calculateMetricData();
  } catch (e) {
    console.error('Ошибка при обновлении:', e);
    error.value = 'Ошибка при расчёте прогресса';
  }
}, { immediate: true });

onMounted(async () => {
  if (props.dataItem) return;
  loading.value = true;

  try {
    const metricExprs = props.metrics.map(m => m.value).concat(props.metrics.map(m => m.max || ''));
    const rankExprs = props.ranks.flatMap(r => Object.values(r.levels));
    const allExprs = [...metricExprs, ...rankExprs];
    const allKeys = new Set(allExprs.flatMap(expr => extractMetricNames(expr)));
    fetchedItem.value = await fetchFullProperties(Array.from(allKeys), props.treeId);
  } catch (e) {
    error.value = apiErrorToString(e);
    console.error('Ошибка при загрузке данных:', e);
  } finally {
    loading.value = false;
  }
});
</script>
